import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin } from "lucide-react";

const exploreLinks = [
  { label: "Destinations", path: "/destinations" },
  { label: "Current Opportunities", path: "/opportunities" },
  { label: "Selected Developments", path: "/developments" },
  { label: "Lifestyle & Investment Guidance", path: "/advisory" },
  { label: "ACRE Insights", path: "/insights" },
];

const destinationLinks = [
  { label: "Dubai", path: "/destinations/dubai" },
  { label: "Abu Dhabi", path: "/destinations/abu-dhabi" },
  { label: "Ras Al Khaimah & Al Marjan Island", path: "/destinations/ras-al-khaimah" },
];

const companyLinks = [
  { label: "Client Stories", path: "/client-stories" },
  { label: "Founders & Team", path: "/about" },
  { label: "Contact", path: "/private-consultation" },
];

const legalLinks = [
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms of Use", path: "/terms-of-use" },
  { label: "Disclaimer", path: "/disclaimer" },
  { label: "Cookie Policy", path: "/cookie-policy" },
  { label: "Sitemap", path: "/sitemap" },
];

const FooterColumn = ({ title, links }) => (
  <div>
    <h3 className="text-xs uppercase tracking-[0.25em] text-[#C5A880] font-semibold mb-5">{title}</h3>
    <ul className="space-y-3">
      {links.map((l) => (
        <li key={l.path + l.label}>
          <Link
            to={l.path}
            data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="text-sm text-[#9DA3AE] hover:text-[#E6CA65] transition-colors duration-300"
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => (
  <footer className="bg-[#08090D] border-t border-[rgba(197,168,128,0.15)]" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-5">
            <img src="/images/logo.png" alt="ACRE — Al Casa Real Estate" className="h-12 w-auto object-contain" />
          </div>
          <p className="text-sm text-[#9DA3AE] leading-relaxed font-light max-w-sm">
            Al Casa Real Estate is a UAE property investment advisory and brokerage firm helping global investors access
            curated opportunities across Dubai, Abu Dhabi, Ras Al Khaimah and Al Marjan Island.
          </p>
          <div className="mt-6 space-y-2.5 text-sm text-[#9DA3AE]">
            <a href="tel:+971585862273" data-testid="footer-phone" className="flex items-center gap-2.5 hover:text-[#E6CA65] transition-colors">
              <Phone size={14} className="text-[#C5A880]" /> +971 58 586 2273
            </a>
            <a href="mailto:ceo@alcasa.ae" data-testid="footer-email" className="flex items-center gap-2.5 hover:text-[#E6CA65] transition-colors">
              <Mail size={14} className="text-[#C5A880]" /> ceo@alcasa.ae
            </a>
            <p className="flex items-start gap-2.5">
              <MapPin size={14} className="text-[#C5A880] mt-0.5 shrink-0" /> London &amp; UAE | Private consultations by appointment
            </p>
          </div>
          <div className="flex gap-3.5 mt-6">
            <a href="https://instagram.com/alcasarealestate" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="social-btn social-ig w-10 h-10 rounded-full flex items-center justify-center">
              <Instagram size={17} className="text-white" />
            </a>
            <a href="https://facebook.com/alcasarealestate" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="social-facebook" className="social-btn social-fb w-10 h-10 rounded-full flex items-center justify-center">
              <Facebook size={17} className="text-white" fill="currentColor" strokeWidth={0.5} />
            </a>
            <a href="https://linkedin.com/company/acre1/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="social-linkedin" className="social-btn social-li w-10 h-10 rounded-full flex items-center justify-center">
              <Linkedin size={17} className="text-white" fill="currentColor" strokeWidth={0.5} />
            </a>
          </div>
        </div>
        <FooterColumn title="Explore" links={exploreLinks} />
        <FooterColumn title="Destinations" links={destinationLinks} />
        <FooterColumn title="Company" links={companyLinks} />
        <FooterColumn title="Legal" links={legalLinks} />
      </div>
      <div className="gold-hairline mt-14 mb-6" />
      <p className="text-xs text-[#6b7280] leading-relaxed max-w-4xl font-light">
        Information on this website is for general guidance only and should not be treated as financial, legal, tax or
        immigration advice. Property pricing, availability, payment plans, visa eligibility, tax treatment and
        investment outcomes are subject to change and depend on individual circumstances. Buyers should seek
        independent professional advice where required.
      </p>
      <p className="text-xs text-[#6b7280] mt-4">© {new Date().getFullYear()} Al Casa Real Estate LLC. All rights reserved.</p>
    </div>
  </footer>
);

export default Footer;
